import type { ComponentChildren } from 'preact'
import { useState, useEffect } from 'preact/hooks'
import { X, ChevronRight, ChevronLeft, Loader2, CheckCircle, Settings, Unplug, AlertTriangle } from 'lucide-preact'
import { useHubSpotAuth } from '@/hooks/useHubSpotAuth'
import { hubspotApi } from '@/api/hubspot'
import { useToast } from '@/hooks/useToast'
import type { HubSpotMappingConfig, HubSpotProperty } from '@/types/hubspot'
import { IntegrationStatusBadge, IntegrationErrorDetail } from '../components/health'
import { ConnectStep } from './ConnectStep'
import { SyncModelStep } from './SyncModelStep'
import { FieldMappingStep } from './FieldMappingStep'
import { NewLeadBehaviorStep } from './NewLeadBehaviorStep'
import { ExistingLeadBehaviorStep } from './ExistingLeadBehaviorStep'
import { DealDataSyncStep } from './DealDataSyncStep'

type WizardStep = 'connect' | 'syncModel' | 'fieldMapping' | 'newLead' | 'existingLead' | 'dealSync'
type PanelView = 'summary' | 'wizard'

const CONFIG_STEPS: WizardStep[] = ['syncModel', 'fieldMapping', 'newLead', 'existingLead', 'dealSync']
const ALL_STEPS: WizardStep[] = ['connect', ...CONFIG_STEPS]
const STEP_LABELS: Record<WizardStep, string> = {
    connect: 'Connect',
    syncModel: 'Sync Model',
    fieldMapping: 'Field Mapping',
    newLead: 'New Lead Behavior',
    existingLead: 'Existing Lead Behavior',
    dealSync: 'Deal Data Sync',
}

interface HubSpotSetupWizardProps {
    isOpen: boolean
    /** The CRM already holding the company's slot, when there is one. */
    blockedByCrm?: string | null
    clientCompanyId: string
    onClose: () => void
    onComplete: () => void
}

export function HubSpotSetupWizard({ isOpen, blockedByCrm, clientCompanyId, onClose, onComplete }: HubSpotSetupWizardProps) {
    const {
        isConnected, connectionId, isConfigured, configuredAtUtc, marketingEventsAvailable,
        status, isSoftFailure, consecutiveFailureCount, recentErrors, aiSummary,
        hubId, connectedAtUtc, lastOutboundSyncUtc, lastInboundSyncUtc,
        isLoading: authLoading, isConnecting, connect, disconnect, refreshStatus,
    } = useHubSpotAuth(clientCompanyId, isOpen)
    const { showSuccess, showError } = useToast()

    const [view, setView] = useState<PanelView>('wizard')
    const [step, setStep] = useState<WizardStep>('connect')
    const [config, setConfig] = useState<HubSpotMappingConfig | null>(null)
    const [properties, setProperties] = useState<HubSpotProperty[]>([])
    // Config/properties load phase for the current open. 'idle' before the fetch is armed
    // counts as not-ready, which closes the race where the spinner would otherwise drop
    // for a frame before the fetch effect sets a loading flag.
    const [configPhase, setConfigPhase] = useState<'idle' | 'loading' | 'done'>('idle')
    const [saving, setSaving] = useState(false)
    // Holds an on-demand-generated AI summary so reopening the summary within the same
    // session (e.g. after a reconfigure round-trip) reuses it instead of re-fetching.
    const [aiSummaryOverride, setAiSummaryOverride] = useState<string | null>(null)
    // True once the initial view has been decided for this open. Kept as state (not a ref)
    // so the body stays on the spinner until the decision lands — no connect/summary flash.
    const [initialized, setInitialized] = useState(false)

    // Reset state when panel closes
    useEffect(() => {
        if (isOpen) return
        setView('wizard')
        setStep('connect')
        setConfig(null)
        setProperties([])
        setConfigPhase('idle')
        setSaving(false)
        setAiSummaryOverride(null)
        setInitialized(false)
    }, [isOpen])

    // Load config and properties once the status fetch has resolved. Waits on authLoading
    // so it reads a fresh isConnected/isConfigured rather than last session's stale status,
    // and marks the config phase 'done' when there's nothing to load (not connected).
    useEffect(() => {
        if (!isOpen || authLoading) return
        if (!isConnected) {
            setConfigPhase('done')
            return
        }
        let cancelled = false
        const loadData = async () => {
            setConfigPhase('loading')
            try {
                const [configRes, propsRes] = await Promise.all([
                    hubspotApi.getConfig(clientCompanyId),
                    hubspotApi.getProperties(clientCompanyId),
                ])
                if (cancelled) return
                if (configRes.data) {
                    // Default deal sync to enabled for new configurations
                    if (!isConfigured) configRes.data.DealConfig.Enabled = true
                    setConfig(configRes.data)
                }
                if (propsRes.data) setProperties(propsRes.data)
            } catch (err) {
                console.error('Failed to load HubSpot config:', err)
            } finally {
                if (!cancelled) setConfigPhase('done')
            }
        }
        loadData()
        return () => { cancelled = true }
    }, [isOpen, authLoading, isConnected, isConfigured, clientCompanyId])

    // Everything needed to render the panel body is loaded: status resolved and, when
    // connected, config too. Drives the spinner so only spinner → final view is shown.
    const loading = authLoading || configPhase !== 'done'

    // Decide the initial view once, only after loading completes so the decision is made
    // against fresh status/config (never stale state from a previous open):
    // - Already configured → summary
    // - Connected but not configured → wizard at syncModel
    // - Not connected → wizard at connect step
    useEffect(() => {
        if (loading || initialized) return
        if (isConnected && isConfigured) {
            setView('summary')
        } else if (isConnected && config) {
            // Config loaded successfully — enter the configuration wizard. If the config
            // fetch failed (config still null despite loading being done), fall through to
            // the connect step rather than a blank wizard body whose steps need config.
            setView('wizard')
            setStep('syncModel')
        } else {
            setView('wizard')
            setStep('connect')
        }
        setInitialized(true)
    }, [loading, initialized, isConnected, isConfigured, config])

    // Show the spinner until data is loaded AND the initial view has been decided, so the
    // body transitions straight from spinner to the final view with no intermediate flash.
    const busy = loading || !initialized

    // Determine steps based on whether we're doing full setup or reconfiguring
    const wizardSteps = view === 'wizard' && isConnected && step !== 'connect' ? CONFIG_STEPS : ALL_STEPS
    const currentStepIndex = wizardSteps.indexOf(step)
    const isFirstStep = currentStepIndex === 0
    const isLastStep = currentStepIndex === wizardSteps.length - 1

    // Header caption: a single computed value avoids a nested ternary in the JSX below.
    let headerCaption = `Step ${currentStepIndex + 1} of ${wizardSteps.length}: ${STEP_LABELS[step]}`
    if (busy) headerCaption = 'Loading…'
    else if (view === 'summary') headerCaption = 'Connection overview'

    const goNext = () => {
        if (!isLastStep) setStep(wizardSteps[currentStepIndex + 1])
    }
    const goBack = () => {
        if (!isFirstStep) setStep(wizardSteps[currentStepIndex - 1])
    }

    const enterConfigWizard = () => {
        setView('wizard')
        setStep('syncModel')
    }

    const handleSave = async () => {
        if (!config) return
        setSaving(true)
        try {
            const res = await hubspotApi.saveConfig(clientCompanyId, config)
            if (res.error) {
                showError('Save Failed', res.error)
            } else {
                showSuccess('Configuration Saved', 'HubSpot integration configured successfully.')
                refreshStatus()
                onComplete()
            }
        } catch {
            showError('Save Failed', 'An unexpected error occurred.')
        } finally {
            setSaving(false)
        }
    }

    if (!isOpen) return null

    const assetBase = (import.meta as ImportMeta).env?.VITE_ASSETS_BASE_URL?.replace(/\/$/, '') || ''

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm animate-fade-in" onClick={onClose} />

            {/* Panel */}
            <div className="fixed inset-y-0 right-0 z-50 w-full md:w-1/2 bg-white dark:bg-slate-900 shadow-2xl flex flex-col animate-slide-in">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-3">
                        <img src={`${assetBase}/assets/integrations/hubspot-logo.svg`} alt="HubSpot" className="w-8 h-8" />
                        <div>
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">HubSpot Integration</h2>
                            <p className="text-xs text-slate-500 dark:text-slate-400">{headerCaption}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Step progress bar (wizard only) */}
                {!busy && view === 'wizard' && (
                    <div className="px-6 py-3 border-b border-slate-100 dark:border-slate-800">
                        <div className="flex gap-1">
                            {wizardSteps.map((s, i) => (
                                <div
                                    key={s}
                                    className={`h-1 flex-1 rounded-full transition-colors ${
                                        i <= currentStepIndex
                                            ? 'bg-primary-500'
                                            : 'bg-slate-200 dark:bg-slate-700'
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-6">
                    {busy ? (
                        <div className="flex items-center justify-center h-full">
                            <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
                        </div>
                    ) : view === 'summary' ? (
                        <div className="space-y-6">
                            <ConnectionStatusCard
                                isConnected={isConnected}
                                isConfigured={isConfigured}
                                configuredAtUtc={configuredAtUtc}
                                hubId={hubId}
                                connectedAtUtc={connectedAtUtc}
                                healthStatus={status}
                                isSoftFailure={isSoftFailure}
                            />
                            <ConfigurationCard
                                isConfigured={isConfigured}
                                configuredAtUtc={configuredAtUtc}
                                config={config}
                                lastOutboundSyncUtc={lastOutboundSyncUtc}
                                lastInboundSyncUtc={lastInboundSyncUtc}
                            />
                            {/* Health / recent errors — mirrors the standard integration panel */}
                            {recentErrors.length > 0 && (
                                <IntegrationErrorDetail
                                    status={status}
                                    isSoftFailure={isSoftFailure}
                                    consecutiveFailureCount={consecutiveFailureCount}
                                    recentErrors={recentErrors}
                                    aiSummary={aiSummaryOverride ?? aiSummary}
                                    clientCompanyId={clientCompanyId}
                                    connectionId={connectionId}
                                    onSummaryFetched={setAiSummaryOverride}
                                />
                            )}
                        </div>
                    ) : (
                        <>
                            {step === 'connect' && (
                                <ConnectStep
                                    isConnected={isConnected}
                                    isConnecting={isConnecting}
                                    blockedByCrm={blockedByCrm}
                                    onConnect={connect}
                                    onDisconnect={disconnect}
                                />
                            )}
                            {step === 'syncModel' && config && (
                                <SyncModelStep
                                    config={config}
                                    marketingEventsAvailable={marketingEventsAvailable}
                                    onChange={setConfig}
                                />
                            )}
                            {step === 'fieldMapping' && config && (
                                <FieldMappingStep
                                    config={config}
                                    properties={properties}
                                    onChange={setConfig}
                                />
                            )}
                            {step === 'newLead' && config && (
                                <NewLeadBehaviorStep config={config} onChange={setConfig} />
                            )}
                            {step === 'existingLead' && config && (
                                <ExistingLeadBehaviorStep config={config} onChange={setConfig} />
                            )}
                            {step === 'dealSync' && config && (
                                <DealDataSyncStep config={config} onChange={setConfig} />
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-700">
                    {busy ? (
                        <>
                            <div />
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600"
                            >
                                Close
                            </button>
                        </>
                    ) : view === 'summary' ? (
                        <>
                            <div>
                                {isConnected && (
                                    <button
                                        type="button"
                                        onClick={disconnect}
                                        className="px-4 py-2 text-sm font-medium rounded-lg border border-red-300 dark:border-red-600 bg-white dark:bg-slate-800 text-red-600 dark:text-red-400 flex items-center gap-2 hover:bg-red-50 dark:hover:bg-red-900/30"
                                    >
                                        <Unplug className="w-4 h-4" /> Disconnect HubSpot
                                    </button>
                                )}
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600"
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    onClick={enterConfigWizard}
                                    className={`px-5 py-2 text-sm font-semibold rounded-lg flex items-center gap-2 ${
                                        isConfigured
                                            ? 'border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600'
                                            : 'bg-primary-600 hover:bg-primary-700 text-white'
                                    }`}
                                >
                                    <Settings className="w-4 h-4" />
                                    {isConfigured ? 'Reconfigure Integration' : 'Complete Configuration'}
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div>
                                {step === 'connect' ? (
                                    <button onClick={onClose} className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
                                        Cancel Setup
                                    </button>
                                ) : isFirstStep ? (
                                    isConfigured ? (
                                        <button
                                            onClick={() => setView('summary')}
                                            className="inline-flex items-center gap-1 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                                        >
                                            <ChevronLeft className="w-4 h-4" /> Back to Overview
                                        </button>
                                    ) : (
                                        <div />
                                    )
                                ) : (
                                    <button onClick={goBack} className="inline-flex items-center gap-1 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white">
                                        <ChevronLeft className="w-4 h-4" /> Back
                                    </button>
                                )}
                            </div>
                            <div>
                                {step === 'connect' ? (
                                    <button
                                        onClick={goNext}
                                        disabled={!isConnected}
                                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        Continue <ChevronRight className="w-4 h-4" />
                                    </button>
                                ) : isLastStep ? (
                                    <button
                                        onClick={handleSave}
                                        disabled={saving}
                                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
                                    >
                                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                        Save Configuration
                                    </button>
                                ) : (
                                    <button
                                        onClick={goNext}
                                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
                                    >
                                        Continue <ChevronRight className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    )
}

const SYNC_MODEL_LABELS: Record<string, string> = {
    MarketingEvents: 'Marketing Events',
    CustomFields: 'Custom Fields',
}

const WRITE_MODE_LABELS: Record<string, string> = {
    MappedFieldsOnly: 'Mapped fields only',
    MappedFieldsAndStandardData: 'Mapped fields + standard data',
}

// Date only (connected/configured dates). Falls back to an em dash when absent/unparseable.
function formatDate(iso?: string | null): string {
    if (!iso) return '—'
    const d = new Date(iso)
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString()
}

// Date + time for sync timestamps. "Never" reads better than a dash for a sync that
// hasn't run yet.
function formatSync(iso?: string | null): string {
    if (!iso) return 'Never'
    const d = new Date(iso)
    return isNaN(d.getTime()) ? 'Never' : d.toLocaleString()
}

function SummaryRow({ label, value }: { label: string; value: ComponentChildren }) {
    return (
        <li className="flex items-start justify-between gap-3">
            <span className="text-slate-500 dark:text-slate-400">{label}</span>
            <span className="text-slate-800 dark:text-slate-200 font-medium text-right">{value}</span>
        </li>
    )
}

function ConfigSummary({ config, lastOutboundSyncUtc, lastInboundSyncUtc }: {
    config: HubSpotMappingConfig
    lastOutboundSyncUtc: string | null
    lastInboundSyncUtc: string | null
}) {
    const enabledMappings = config.FieldMappings.filter(m => m.IsEnabled).length
    const overwriteCount = config.ExistingLeadBehavior.OverwriteRules.filter(r => r.OverwriteExisting).length
    return (
        <ul className="space-y-2 text-xs">
            <SummaryRow label="Sync model" value={SYNC_MODEL_LABELS[config.SyncModel] ?? config.SyncModel} />
            <SummaryRow label="Field mappings" value={`${enabledMappings} enabled`} />
            <SummaryRow
                label="New leads"
                value={WRITE_MODE_LABELS[config.NewLeadBehavior.WriteMode] ?? config.NewLeadBehavior.WriteMode}
            />
            <SummaryRow
                label="Existing leads"
                value={overwriteCount > 0 ? `${overwriteCount} field${overwriteCount === 1 ? '' : 's'} overwrite existing` : 'Preserve existing values'}
            />
            <SummaryRow label="Deal sync" value={config.DealConfig.Enabled ? 'On' : 'Off'} />
            <SummaryRow label="Last outbound sync" value={formatSync(lastOutboundSyncUtc)} />
            <SummaryRow label="Last inbound sync" value={formatSync(lastInboundSyncUtc)} />
        </ul>
    )
}

function ConnectionStatusCard({ isConnected, isConfigured, configuredAtUtc, hubId, connectedAtUtc, healthStatus, isSoftFailure }: {
    isConnected: boolean
    isConfigured: boolean
    configuredAtUtc: string | null
    hubId: string | null
    connectedAtUtc: string | null
    healthStatus: string
    isSoftFailure: boolean
}) {
    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-5 space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    {isConnected ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                    )}
                    <div>
                        <h4 className="font-medium text-sm text-slate-900 dark:text-white">Connection Status</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            {isConnected ? 'HubSpot is connected and active' : 'HubSpot is not connected'}
                        </p>
                    </div>
                </div>
                <span className={`text-[10px] px-2.5 py-1 rounded-md font-medium ${
                    isConnected
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                }`}>
                    {isConnected ? 'Connected' : 'Disconnected'}
                </span>
            </div>

            {isConnected && (
                <ul className="space-y-2 text-xs border-t border-slate-100 dark:border-slate-800 pt-4">
                    {hubId && <SummaryRow label="Hub ID" value={hubId} />}
                    <SummaryRow label="Connected" value={formatDate(connectedAtUtc)} />
                    <SummaryRow label="Configured" value={isConfigured ? formatDate(configuredAtUtc) : 'Not configured'} />
                    {healthStatus && (
                        <SummaryRow
                            label="Sync health"
                            value={<IntegrationStatusBadge status={healthStatus} isSoftFailure={isSoftFailure} />}
                        />
                    )}
                </ul>
            )}
        </div>
    )
}

function ConfigurationCard({ isConfigured, configuredAtUtc, config, lastOutboundSyncUtc, lastInboundSyncUtc }: {
    isConfigured: boolean
    configuredAtUtc: string | null
    config: HubSpotMappingConfig | null
    lastOutboundSyncUtc: string | null
    lastInboundSyncUtc: string | null
}) {
    const caption = isConfigured
        ? `Configured on ${formatDate(configuredAtUtc)}`
        : 'Configuration required — complete setup to begin syncing data'
    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-5 space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    {isConfigured ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                    )}
                    <div>
                        <h4 className="font-medium text-sm text-slate-900 dark:text-white">Configuration</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{caption}</p>
                    </div>
                </div>
                <span className={`text-[10px] px-2.5 py-1 rounded-md font-medium ${
                    isConfigured
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                }`}>
                    {isConfigured ? 'Complete' : 'Incomplete'}
                </span>
            </div>

            {isConfigured && config && (
                <div className="border-t border-slate-100 dark:border-slate-800 pt-4">
                    <ConfigSummary
                        config={config}
                        lastOutboundSyncUtc={lastOutboundSyncUtc}
                        lastInboundSyncUtc={lastInboundSyncUtc}
                    />
                </div>
            )}
        </div>
    )
}
